import axios, { AxiosRequestConfig, AxiosInstance, AxiosResponse } from 'axios'
import { addAuthHeaders } from '@umd-dit/oidc-core'

// Get API URL from environment
const API_URL = import.meta.env.VITE_API_URL

// Import workspace utilities directly from the component
import { getActiveWorkspace, getOrganizationId } from '@umd-dit/workspaces-component-react'

// Helper for getting selected workspace IDs - using our central utility
const getSelectedWorkspace = () => {
  const workspace = getActiveWorkspace()
  return { 
    orgId: workspace?.orgId, 
    wsId: workspace?.wsId 
  }
}

// Optional request configuration extensions
interface RequestOptions extends AxiosRequestConfig {
  // Indicates this is a global endpoint (admin/system level)
  isGlobal?: boolean
  // Explicitly indicate this is a workspace-specific endpoint
  isWorkspaceSpecific?: boolean
  // Request parameters
  params?: {
    orgId?: string;
    [key: string]: any;
  }
}

/**
 * Build a URL with the appropriate prefix based on the request type
 *
 * URL Structure Convention:
 * 1. Standard routes: `/app/{appkey}/org/{org_id}/ws/{ws_id}/resource`
 * 2. Admin routes: `/admin/app/{appkey}/resource`
 * 3. User profile: `/app/{appkey}/org/{org_id}/user/profile`
 * 4. Special routes: `/health` and `/oidc/login` (no prefixes)
 */
export const buildUrl = (path: string, options: RequestOptions = {}): string => {
  const { isGlobal, isWorkspaceSpecific } = options
  const cleanPath = path.startsWith('/') ? path : `/${path}`

  // IMPORTANT: There's a nuance with FastAPI path parameters:
  // When using this in the URL directly as a path parameter (e.g., /app/{appkey}/...),
  // FastAPI automatically converts hyphens to underscores in Python variables.
  // So to match `/app/{appkey}/...` in the backend where appkey is 'legislative-review',
  // we'd need to use '/app/legislative_review/...' in direct URL construction.
  //
  // This doesn't affect this buildUrl function since we're just concatenating strings,
  // but be aware if you're skipping this function and constructing URLs directly.
  const appKey = 'legislative-review'  // Default application ID with hyphen to match backend expectations

  // Special routes like /health that shouldn't have app prefix
  if (path.startsWith('/health') || path.startsWith('/oidc/')) {
    return cleanPath;
  }

  // Handle legacy paths with /api/ prefix
  if (path.startsWith('/api/')) {
    return path.replace('/api', '');
  }

  // Handle global (admin) endpoints
  if (isGlobal) {
    return `/admin/app/${appKey}${cleanPath}`
  }

  // Handle workspace-specific endpoints
  if (isWorkspaceSpecific) {
    const { orgId, wsId } = getSelectedWorkspace()
    if (!orgId || !wsId) {
      console.warn('Warning: workspace-specific request, but no workspace selected')
      return cleanPath
    }
    return `/app/${appKey}/org/${orgId}/ws/${wsId}${cleanPath}`
  }

  // Get organization ID using the utility function
  const orgId = getOrganizationId(options.params?.orgId);

  // If path starts with /user, it's likely a user profile or workspace related route
  if (path.startsWith('/user/')) {
    return `/app/${appKey}/org/${orgId}${cleanPath}`;
  }

  // Default case - just prepend with app prefix and appKey
  return `/app/${appKey}${cleanPath}`
}

class ApiClient {
  private instance: AxiosInstance

  constructor(baseURL: string) {
    this.instance = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })

    // Add auth token to all requests
    this.instance.interceptors.request.use((config) => {
      console.log(`API Request to ${config.url}`);
      const currentHeaders = config.headers ? { ...config.headers } : {};
      // Convert axios headers to headers that addAuthHeaders expects
      const fetchOptions: RequestInit = {
        headers: currentHeaders as Record<string, string>,
      };

      // Use the OIDC core's addAuthHeaders function to add the proper auth headers
      const optionsWithAuth = addAuthHeaders(fetchOptions);

       // Apply the headers from optionsWithAuth to the axios config
      if (optionsWithAuth.headers) {
        const authHeaders = optionsWithAuth.headers;

        if (authHeaders instanceof Headers) {
          // Convert Headers object to plain object
          authHeaders.forEach((value, key) => {
            if (config.headers) {
              config.headers[key] = value;
            }
          });
        } else if (typeof authHeaders === 'object') {
          // Handle plain object headers
          Object.entries(authHeaders).forEach(([key, value]) => {
            if (config.headers && typeof value === 'string') {
              config.headers[key] = value;
            }
          });
        }
      }
      
      // Optional: Add debug header
      const isDebugMode = sessionStorage.getItem('debugModeEnabled') === 'true'
      if (isDebugMode && config.headers) {
        config.headers['X-Debug-Mode'] = 'true'
        console.log('[API] Adding X-Debug-Mode header')
      }
      
      return config;
    })

    // Global response handling
    this.instance.interceptors.response.use(
      (response) => response,
      (error) => {
        if (axios.isAxiosError(error)) {
          console.error(`[API] Error ${error.response?.status}: ${error.config?.url}`)
          
          if (error.response?.status === 401 || error.response?.status === 403) {
            console.error(`[API] Authorization error (${error.response.status})`)
            // Dispatch access-denied event to trigger the access denied screen
            document.dispatchEvent(new CustomEvent('access-denied'))
          }
        }
        return Promise.reject(error)
      }
    )
  }

  /**
   * Make a request with proper URL construction based on request type
   */
  public async request<T>(options: RequestOptions): Promise<AxiosResponse<T>> {
    // Build the appropriate URL based on the request type
    if (options.url) {
      options.url = buildUrl(options.url, options)
    }

    // Log the request for debugging
    console.log(`[API] ${options.method || 'GET'} ${options.url}`)

    return this.instance.request<T>(options)
  }

  /**
   * GET request helper
   */
  public async get<T>(url: string, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...options, url, method: 'GET' })
  }

  /**
   * POST request helper
   */
  public async post<T>(url: string, data?: any, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...options, url, data, method: 'POST' })
  }

  /**
   * PUT request helper
   */
  public async put<T>(url: string, data?: any, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...options, url, data, method: 'PUT' })
  }

  /**
   * DELETE request helper
   */
  public async delete<T>(url: string, options: RequestOptions = {}): Promise<AxiosResponse<T>> {
    return this.request<T>({ ...options, url, method: 'DELETE' })
  }
}

// Create a singleton instance
export const apiClient = new ApiClient(API_URL)